/**
 * Will this patient lose PHI access if their token drops below AAL2?
 *
 * ⚠️ THIS EXISTS BECAUSE I RE-DERIVED A CANONICAL RULE AND GOT IT WRONG.
 * Mirrors `mideo-health-app/src/hooks/useWillLoseAccessOnAal2Drop.ts`.
 *
 * `updatePassword` mints a token with no AAL2 claim, so the answer decides
 * whether we sign the patient out afterwards. The first version computed it
 * inline in each host:
 *
 *     (mfa.enforced ?? false) && !hasLiveException   // with a local expiry check
 *
 * ⚠️ `hasValidException` ALREADY EXISTS in `@usacsmideo/types`, beside
 * `decideMfaGate`, whose header says: *"If you find yourself writing
 * `if (!aal2 && enforced)` in ANY consumer, stop: that logic belongs here. Two
 * implementations of a PHI gate is how one of them silently drifts open."*
 *
 * The two copies already disagreed. The canonical `expiresAtMs` handles a
 * `toMillis()` method and a plain epoch-ms number; my version handled neither —
 * `new Date(1789000000000 as string)` is `Invalid Date`, so an exception holder
 * would have been signed out gratuitously. `MfaException.expiresAt` is typed
 * `TimestampLike` today, which always carries `seconds`, so the typed path
 * happened to agree. **Correctness by accident of the current wire shape is not
 * correctness.** Found by adversarial review round 3.
 *
 * ── ⚠️ THE CLOCK ─────────────────────────────────────────────────────────
 *
 * `hasValidException` requires SERVER time and **fails closed** without it —
 * D3 measured why: honouring an exception offline would let a stolen device
 * with a granted exception reach PHI indefinitely in airplane mode.
 *
 * My inline version used `Date.now()`, which `serverTime.ts` forbids in as many
 * words: *"We deliberately do NOT fall back to `Date.now()` anywhere in this
 * file. That value is precisely what N3 exists to distrust."*
 *
 * ⚠️ HERE, FAILING CLOSED MEANS `true`: if we cannot read server time we assume
 * the patient WILL lose access, so we warn and sign out. Over-warning is
 * recoverable (they sign in again); under-warning leaves them in the silent
 * AAL1 state CU-86aknw0ku exists to prevent.
 *
 * ⚠️ STILL ADVISORY. This shapes COPY and a post-change hand-off, never access
 * (M4). `decideMfaGate` remains the sole authority.
 */

import { useEffect, useState } from 'react';
import { hasValidException, type PatientDoc } from '@usacsmideo/types';
import { getServerTimeMs } from '../utils/mfa/serverTime';

export function useWillLoseAccessOnAal2Drop(patientDoc: PatientDoc | undefined): boolean {
  const enforced = patientDoc?.mfa?.enforced ?? false;
  const exception = patientDoc?.mfa?.exception;

  // ⚠️ SEEDED `true` — fail closed while server time is unknown. A patient who
  // is not enforced is corrected to `false` on the first effect run; the
  // reverse default would silently skip a needed sign-out.
  const [willLose, setWillLose] = useState<boolean>(enforced);

  useEffect(() => {
    let cancelled = false;

    // Not enforced ⇒ nothing to lose, and no clock needed to say so.
    if (!enforced) {
      setWillLose(false);
      return;
    }

    void (async () => {
      const serverTimeMs = await getServerTimeMs();
      if (cancelled) return;
      // ⚠️ THE CANONICAL RULE. `hasValidException` returns false for a null
      // clock, so a failed server-time read leaves `willLose` true.
      setWillLose(!hasValidException(exception ?? null, serverTimeMs));
    })();

    return () => {
      cancelled = true;
    };
  }, [enforced, exception]);

  return willLose;
}
